import { DataType, SystemGeneratedTableColumnType } from '@doss/prisma/types'

// [PLAT-367] Move this to codegen. And possibly other similarly defined enums here.
export const ALLOWED_ARRAY_INNER_TYPES = [
  DataType.ENUM,
  DataType.LOOKUP,
  DataType.FILE_URL,
  DataType.USER,
] as const

export const NUMERICAL_COLUMN_TYPES = [
  DataType.NUMBER,
  DataType.FORMULA,
  DataType.FORMULA_NUMBER,
  DataType.LOOKUP,
  DataType.ID,
]

export const DATE_TIME_COLUMN_TYPES = [DataType.DATE_TIME, DataType.DATE]

// Only number and string type lookup columns can be overwritten
export const OVERWRITABLE_LOOKUP_COLUMN_TYPES = [DataType.NUMBER, DataType.STRING]
export type OverwritableLookupColumnTypes = (typeof OVERWRITABLE_LOOKUP_COLUMN_TYPES)[number]

export function isValidNumericalColumnType(
  type: DataType,
): type is (typeof NUMERICAL_COLUMN_TYPES)[number] {
  return NUMERICAL_COLUMN_TYPES.includes(type as (typeof NUMERICAL_COLUMN_TYPES)[number])
}

export function isValidDateTimeColumnType(
  type: DataType,
): type is (typeof DATE_TIME_COLUMN_TYPES)[number] {
  return DATE_TIME_COLUMN_TYPES.includes(type as (typeof DATE_TIME_COLUMN_TYPES)[number])
}

export const DOLLAR_COLUMN_TYPES = [DataType.NUMBER, DataType.FORMULA, DataType.FORMULA_NUMBER]

export function canColumnShowCurrency(type: DataType): boolean {
  return (DOLLAR_COLUMN_TYPES as readonly string[]).includes(type)
}

export const STRING_COLUMN_TYPES = [
  DataType.STRING,
  DataType.ENUM,
  DataType.LOOKUP,
  DataType.ID,
  DataType.DATE,
  DataType.DATE_TIME,
  DataType.FILE_URL,
  DataType.USER,
]

export type AllowedArrayInnerTypes = (typeof ALLOWED_ARRAY_INNER_TYPES)[number]

/**
 * Checks if the inner type is valid to create an ARRAY type column
 */
export function isValidArrayInnerType(
  innerType: DataType | undefined | null,
): innerType is AllowedArrayInnerTypes {
  if (!innerType) {
    return false
  }

  return ALLOWED_ARRAY_INNER_TYPES.includes(innerType as AllowedArrayInnerTypes)
}

/**
 * Checks if the type of a lookup column allows it to be overwritten
 */
export function isOverwritableLookupColumnType(
  type: DataType,
): type is OverwritableLookupColumnTypes {
  return OVERWRITABLE_LOOKUP_COLUMN_TYPES.includes(type as OverwritableLookupColumnTypes)
}

export const SYSTEM_GENERATED_COLUMN_TYPES = Object.values(SystemGeneratedTableColumnType)

export function isSystemGeneratedTableColumnType(
  columnId: string,
): columnId is SystemGeneratedTableColumnType {
  return SYSTEM_GENERATED_COLUMN_TYPES.some((type) => type === columnId)
}

/**
 * Returns the system column key if columnId is a valid system column field id.
 * Frontend uses the enum value directly as the field id (e.g. CREATED_BY, UPDATED_AT).
 */
export function parseSystemColumnFieldId(columnId: string): SystemGeneratedTableColumnType | null {
  if (typeof columnId !== 'string') {
    return null
  }
  return isSystemGeneratedTableColumnType(columnId) ? columnId : null
}

/**
 * Minimal type for a column that has a valid system-generated column type.
 * Use with isSystemGeneratedColumn() so TypeScript narrows correctly without
 * intersecting with complex GraphQL-generated types (which can reduce to never).
 */
export type TableColumnWithSystemGeneratedType = {
  systemGeneratedColumnType: SystemGeneratedTableColumnType
}

/**
 * Type guard: true if the column has a non-null systemGeneratedColumnType that is a valid
 * SystemGeneratedTableColumnType. Narrows to TableColumnWithSystemGeneratedType so callers
 * can safely read column.systemGeneratedColumnType without intersecting with union types.
 */
export function isSystemGeneratedColumn(column: {
  systemGeneratedColumnType?: string | null
}): column is TableColumnWithSystemGeneratedType {
  return (
    column.systemGeneratedColumnType != null &&
    SYSTEM_GENERATED_COLUMN_TYPES.some((type) => type === column.systemGeneratedColumnType)
  )
}
